import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompose';
import { compact, get, startCase } from 'lodash';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import BankIcon from '@material-ui/icons/AccountBalance';
import CardIcon from '@material-ui/icons/CreditCard';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PanToolIcon from '@material-ui/icons/PanTool';

import {
  getCaptureMoment,
  formatPrice,
} from 'helpers/utils';
import immutableToJS from 'helpers/immutableToJS';
import {
  InvoiceBadge,
  QuickHelp,
} from 'components';
import { selectState } from 'redux/selectors';
import { refreshToken } from 'redux/auth/actions';

class OrderStatusBadge extends Component {
  static contextTypes = {
    payOnline: PropTypes.bool,
  };

  static propTypes = {
    formatMessage: PropTypes.func.isRequired,
    info: PropTypes.shape({}).isRequired,
    orderStatus: PropTypes.string.isRequired,
    markAsPaid: PropTypes.func.isRequired,
    refreshToken: PropTypes.func.isRequired,
    orderRequesting: PropTypes.bool,
  };

  static defaultProps = {
    orderRequesting: false,
  };

  state = { dialogOpen: false };

  getPayMethodString = (feeType) => {
    const { formatMessage } = this.props;

    if (feeType === 'manual') {
      return formatMessage('One Time Pay');
    } else if (feeType === 'manual-offline') {
      return formatMessage('Check/Cash');
    } else if (feeType.startsWith('manual-')) {
      if (feeType.substr(7) === 'cod') {
        return formatMessage('C.O.D');
      }
      return formatMessage(startCase(feeType.substr(7)));
    } else if (feeType === 'coin') {
      return formatMessage(startCase('balance'));
    }

    return formatMessage(startCase(feeType));
  };

  render() {
    const { payOnline } = this.context;
    const {
      formatMessage,
      info,
      orderStatus,
      orderRequesting,
      markAsPaid,
      refreshToken,
    } = this.props;
    const { dialogOpen } = this.state;

    const feeType = get(info, 'payMethod') || 'manual';
    const payMethodString = this.getPayMethodString(feeType);
    const feeIcon = {
      card: <CardIcon style={{ marginLeft: 5 }} />,
      ach: <BankIcon style={{ marginLeft: 5 }} />,
      coin: <AttachMoneyIcon style={{ marginLeft: 5 }} />,
    }[feeType] || <PanToolIcon style={{ marginLeft: 5, transform: 'scaleX(-1) rotate(90deg)' }} />;
    const captureMoment = getCaptureMoment(info);
    const processingFee = info.feeTotal - info.feeBasic;
    const processingFeeText = ['coin', 'card', 'ach'].indexOf(info.payMethod) > -1
      ? (Number(processingFee) === 0
        ? ''
        : `${processingFee.toFixed(1)}%`)
      : (get(info, 'delivered') ? '' : '?%');
    const statuses = compact([
      {
        title: formatMessage('Terms'),
        value: payMethodString,
      },
      {
        title: formatMessage('Paid Status'),
        value: formatMessage(orderStatus === 'Confirmed' ? 'Scheduled' : orderStatus),
      },
      payOnline && ({
        title: formatMessage('Money Authorized'),
        value: info.authorized ? `$${formatPrice(get(info, 'transactions.amount') ? get(info, 'transactions.amount') / 100 : info.totalPrice)}` : formatMessage('No'),
      }),
      payOnline && ({
        title: `${formatMessage('Basic Fee Captured')} (${info.feeBasic}%)`,
        value: info.captured ? formatMessage('Yes') : formatMessage('No'),
      }),
      payOnline && (processingFeeText ? {
        title: `${formatMessage('Processing Fee Captured')} (${processingFeeText})`,
        value: ['card', 'ach', 'full'].indexOf(info.feeStatus) > -1 ? formatMessage('Yes') : formatMessage('No'),
      } : {
        title: formatMessage('Processing Fee'),
        value: formatMessage('No Fee'),
      }),
      payOnline && (info.delivered ? {
        title: formatMessage('Money Transfered'),
        value: moment(info.deliveredAt).format('MMM D'),
      } : {
        title: formatMessage('Expected Capture Date'),
        value: info.captureScheduledAt ? moment(info.captureScheduledAt).format('MMM D') : '-',
      }),
    ]);
    return (
      <Fragment>
        {
          <InvoiceBadge type={orderStatus} onClick={() => this.setState({ dialogOpen: true })}>
            <div className="d-flex align-items-center">
              <strong style={{ textAlign: 'left' }}>
                <span style={{ fontSize: 11 }}>{payMethodString.toUpperCase()}</span>{captureMoment}{info.delivered && '✓'}
                {/* <div className="d-flex" style={{ marginTop: 3 }}>
                  <div style={{ width: 32, marginRight: 5 }}>
                    {`-${info.feeBasic}%${info.captured ? '✓' : ' '}`}
                  </div>
                  <div style={{ width: 32 }}>
                    {`${processingFeeText === ''
                        ? processingFeeText
                        : `-${processingFeeText}`
                      }${['full', 'ach', 'card'].indexOf(info.feeStatus) > -1 ? '✓' : ''}`
                    }
                  </div>
                </div> */}
              </strong>
              {feeIcon}
            </div>
          </InvoiceBadge>
        }
        <Dialog open={dialogOpen} onClose={() => this.setState({ dialogOpen: false })}>
          <DialogContent>
            <h3>{formatMessage('Order Status')} #{info.orderNum}</h3>
            {statuses.map((status, index) => (
              <div className="d-flex align-items-center justify-content-between" key={index}>
                <div>{status.title}</div>
                <strong className="ml-4">{status.value}</strong>
              </div>
            ))}
          </DialogContent>
          <DialogActions>
            {info.manualPay && info.type === 'SO' && !info.delivered && (
              <Fragment>
                <Button
                  color="secondary"
                  onClick={() => markAsPaid(() => {
                    this.setState({ dialogOpen: false });
                    refreshToken();
                  })}
                  variant="outlined"
                  disabled={info.mismatch || info.invalid || orderRequesting}
                >
                  {formatMessage('Mark as Paid')}
                </Button>
                <QuickHelp style={{ marginLeft: -10, marginTop: -40 }}>
                  {(info.mismatch || info.invalid)
                    ? formatMessage('You can not mark it as paid because this is mismatching. Please check if you placed/corrected the orders(AO) to farms related to this invoice.')
                    : (
                      <Fragment>
                        {formatMessage('Invoice will be marked as paid.')}
                      </Fragment>
                    )
                  }
                </QuickHelp>
              </Fragment>
            )}
            <Button
              color="primary"
              onClick={() => this.setState({ dialogOpen: false })}
              variant="contained"
              disabled={orderRequesting}
            >
              {formatMessage('Close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  }
}
/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('orders', 'order')(state),
});

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
  refreshToken: () => dispatch(refreshToken()),
});

export default compose(
  setDisplayName('OrderStatusBadge'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  immutableToJS,
)(OrderStatusBadge);
